import { DafPrivilegeTypeItem } from '@generated'
import { useTranslation } from 'next-i18next'
import { FC } from 'react'
import { Item, getKontentImage } from '@lib/kontent'
import { PrivilegeItem } from '@lib/kontent/models/PrivilegeItem'
import RichContentField from '@components/_content-page/RichContentField'
import { Button, OldHeading, Icon, Image, SpecialSpan, OldText, theme } from '@gassan-ui'
import { Modal } from '@components/Modal'
import { usePrivilegeItem } from './hooks/use-privilege-item'

type PrivilegeSliderItemModalProps = Item<PrivilegeItem> & {
  open: boolean
  handleRequestClose: () => void
  modularContent: { [key: string]: any }
  dafItem: DafPrivilegeTypeItem
}

export const PrivilegeSliderItemModal: FC<PrivilegeSliderItemModalProps> = ({
  open,
  handleRequestClose,
  elements,
  modularContent,
  dafItem,
}) => {
  const { t } = useTranslation('account')
  const { buttonStatus, handleAddToCart } = usePrivilegeItem({
    dafItem,
    elements,
    handleRequestClose,
  })
  const richContent = modularContent[elements.rich_content.value[0]]
  const isReservation = dafItem.privilege_action === 'RESERVATION'

  if (richContent.elements.content.type !== 'rich_text') return null

  return (
    <Modal onOpenChange={handleRequestClose} title={t('privileges.congratulations')} open={open}>
      <div className="relative w-full">
        <div className="w-full shrink-0 transition-opacity group-hover:opacity-80">
          <Image
            className="aspect-[10/6]"
            sources={[
              getKontentImage(elements.image___placeholder_image.value[0].url, {
                width: 600,
              }),
            ]}
            alt={elements.image___placeholder_image.value[0].description as string}
            placeholderSrc={getKontentImage(elements.image___placeholder_image.value[0].url, {
              width: 50,
            })}
          />
        </div>
        <div className="pt-4 md:pt-6">
          <OldHeading variant="h1" as="h2" mb="1rem" className="md:!text-[2rem]">
            {elements.title.value}
          </OldHeading>
          <RichContentField {...richContent.elements.content} />
          {isReservation && (
            <div className="mt-4 w-full bg-pampas p-4">
              <OldText mb="0">{t('privileges.modalText')}</OldText>
            </div>
          )}
          {['redeemed', 'in-cart'].includes(buttonStatus) ? (
            <div className="flex items-center text-bigStone" data-bottom-element>
              <Icon icon="account-check" color={theme.colors.success} />
              <SpecialSpan variant="two" className="!text-[11px]">
                {t(`privileges.${buttonStatus}`, { ns: 'account' })}
              </SpecialSpan>
            </div>
          ) : (
            <Button
              variant="dark"
              className="mt-3"
              status={buttonStatus as any}
              onClick={handleClick}
              data-bottom-element
            >
              {dafItem.privilege_action === 'RESERVATION'
                ? t(`privileges.${dafItem.privilege_action}`, { ns: 'account' })
                : t(`privileges.${dafItem.privilege_action}`, { ns: 'account' })}
            </Button>
          )}
        </div>
      </div>
    </Modal>
  )

  function handleClick() {
    switch (dafItem.privilege_action) {
      case 'RESERVATION':
        return window.open(
          `mailto:customercare@gassan.com?subject=${t('privileges.emailRedeem')}${
            dafItem.description
          }`,
        )
      case 'PartnerURL':
        if (elements.partner_url__optional_.value) {
          window.open(elements.partner_url__optional_.value)
        }
        break
      case 'EVENTS':
        if (elements.event_url__optional_.value) {
          window.open(elements.event_url__optional_.value)
        }
        break
      case 'SALESORDER':
        handleAddToCart()
        break
      default:
        break
    }
  }
}
